import type { CheckPromoDisplayQuery } from 'api/promo'
import { getAllVouchers, getChannelMarkupByFilters, getChannelMarkupById, getMyVouchers, getPromoDisplayConfig, getPromoDisplayConfigValidation } from 'api/promo'
import { stateToDiscountOrder } from 'lib/promo/promoMappers'
import { isSpoofed } from 'selectors/isSpoofed'
import {
  API_CALL,
  API_CALL_SUCCESS,
  CHECKOUT_FETCHING_VOUCHERS,
  FETCH_ALL_VOUCHERS,
  FETCH_CHANNEL_MARKUP,
  FETCH_META_OFFERS_DISCOUNT,
  CLEAR_OFFERS_WITH_DISCOUNT,
  SET_METASEARCH_PROPERTY_BLACKLIST,
} from './actionConstants'
import {
  FETCH_NBL_COMPETITION_PRIZE,
  FETCH_NBL_COMPETITION_OBJECT,
  FETCH_NBL_COMPETITION_USER_STATUS,
  FETCH_UK_COMPETITION_PRIZE,
  FETCH_UK_COMPETITION_OBJECT,
  FETCH_UK_COMPETITION_USER_STATUS,
  FETCH_AU_COMPETITION_PRIZE,
  FETCH_AU_COMPETITION_OBJECT,
  FETCH_AU_COMPETITION_USER_STATUS,
  FETCH_INTL_COMPETITION_PRIZE,
  FETCH_INTL_COMPETITION_OBJECT,
  FETCH_INTL_COMPETITION_USER_STATUS,
  FETCH_PROMO_DISPLAY_CONFIGS,
  REMOVE_PROMO_DISPLAY_CONFIG,
  RESET_TARGETED_PROMO_DETAILS,
  CHECK_PROMO_DISPLAY_CONFIG,
  FETCH_METASEARCH_OFFER,
} from './apiActionConstants'
import type {
  GetMetaOffersDiscountParams,
  GetOfferWithDiscountParams,
} from 'marketing/api'
import {
  getNblCompetitionPrize,
  getNblCompetitionObject,
  getNblCompetitionUserStatus,
  getUkCompetitionPrize,
  getUkCompetitionObject,
  getUkCompetitionUserStatus,
  getAuCompetitionPrize,
  getAuCompetitionObject,
  getAuCompetitionUserStatus,
  getIntlCompetitionPrize,
  getIntlCompetitionObject,
  getIntlCompetitionUserStatus,
  getOfferDiscount,
  getOfferWithDiscount,
} from 'marketing/api'
import type { paths } from '@luxuryescapes/contract-svc-promo'
import type { AppAction } from './types/ActionTypes'
import { trackEvent } from 'analytics/analytics'
import { promotionViewEvent } from 'analytics/snowplow/events'
import config from 'constants/config'
import { addPropertyToBlacklist, serializeBlacklist, METASEARCH_PROPERTY_BLACKLIST_COOKIE } from 'lib/metasearch/metasearchBlacklist'
import { setCookie } from 'lib/web/cookieUtils'
import { hoursInSeconds } from 'lib/datetime/durationInSeconds'

export function fetchNblCompetitionPrize(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_NBL_COMPETITION_PRIZE,
    request: () => getNblCompetitionPrize(),
  }
}

export function fetchNblCompetitionObject(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_NBL_COMPETITION_OBJECT,
    request: () => getNblCompetitionObject(),
  }
}

export function fetchNblCompetitionUserStatus(accessToken?: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_NBL_COMPETITION_USER_STATUS,
    request: () => getNblCompetitionUserStatus(accessToken),
  }
}

export function fetchUkCompetitionPrize(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_UK_COMPETITION_PRIZE,
    request: () => getUkCompetitionPrize(),
  }
}

export function fetchUkCompetitionObject(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_UK_COMPETITION_OBJECT,
    request: () => getUkCompetitionObject(),
  }
}

export function fetchUkCompetitionUserStatus(accessToken?: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_UK_COMPETITION_USER_STATUS,
    request: () => getUkCompetitionUserStatus(accessToken),
  }
}

export function fetchAuCompetitionPrize(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_AU_COMPETITION_PRIZE,
    request: () => getAuCompetitionPrize(),
  }
}

export function fetchAuCompetitionObject(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_AU_COMPETITION_OBJECT,
    request: () => getAuCompetitionObject(),
  }
}

export function fetchAuCompetitionUserStatus(region?: string, accessToken?: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_AU_COMPETITION_USER_STATUS,
    request: () => getAuCompetitionUserStatus(region, accessToken),
  }
}

export function fetchIntlCompetitionPrize(region?: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_INTL_COMPETITION_PRIZE,
    request: () => getIntlCompetitionPrize(region),
  }
}

export function fetchIntlCompetitionObject(region: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_INTL_COMPETITION_OBJECT,
    request: () => getIntlCompetitionObject(region),
  }
}

export function fetchIntlCompetitionUserStatus(region?: string, accessToken?: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_INTL_COMPETITION_USER_STATUS,
    request: () => getIntlCompetitionUserStatus(region, accessToken),
  }
}

export function fetchChannelMarkupAction(channelMarkupId: string | undefined, filters: App.ChannelMarkupFilters | undefined): AppAction {
  return {
    type: API_CALL,
    api: FETCH_CHANNEL_MARKUP,
    request: async() => {
      let channelMarkup: App.ChannelMarkup | undefined
      if (channelMarkupId) {
        channelMarkup = await getChannelMarkupById(channelMarkupId)
      } else if (!channelMarkupId && filters?.source.length && filters.medium.length && filters.campaign.length) {
        channelMarkup = await getChannelMarkupByFilters(filters)
      }
      return channelMarkup
    },
  }
}

export function fetchMetaOffersDiscount(params: GetMetaOffersDiscountParams): AppAction {
  return {
    type: API_CALL,
    api: FETCH_META_OFFERS_DISCOUNT,
    request: () => getOfferDiscount(params).then((response) => ({
      offerId: params.offerId,
      packages: response.packages,
    })),
  }
}

export function getOfferWithDiscountKey(offerId: string, checkIn?: string, checkOut?: string): string {
  return `${offerId}:${checkIn ?? ''}:${checkOut ?? ''}`
}

export function clearOffersWithDiscount(): { type: typeof CLEAR_OFFERS_WITH_DISCOUNT } {
  return { type: CLEAR_OFFERS_WITH_DISCOUNT }
}

export function fetchMetaSearchOffer(
  offerId: string,
  params: GetOfferWithDiscountParams,
  regionCode: string,
): AppAction {
  const key = getOfferWithDiscountKey(offerId, params.checkIn, params.checkOut)
  return {
    type: API_CALL,
    api: FETCH_METASEARCH_OFFER,
    key,
    offerId,
    request: () => getOfferWithDiscount(offerId, params, regionCode).then((response) => ({
      key,
      offerId,
      offer: response.offer,
      discountedPackages: response.discountedPackages,
    })),
  }
}

export function fetchMyVouchers(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (!config.APPLY_VOUCHER_ENABLED) {
      return
    }
    const discountRequestOrder = stateToDiscountOrder(state)
    if (state.checkout.cart.items.length > 0 && discountRequestOrder.items.length === 0) {
      return
    }
    dispatch({
      type: API_CALL,
      api: CHECKOUT_FETCHING_VOUCHERS,
      request: () => getMyVouchers({
        region: state.geo.currentRegionCode,
        discountRequest: {
          order: discountRequestOrder,
          userId: state.auth.account.memberId as string,
          requestSource: isSpoofed(state) ? 'global-checkout-spoof' : 'global-checkout',
        },
        usableVouchersOnly: true,
      }),
    })
  }
}

export function fetchPromoDisplay(params: Omit<paths['/api/promo/promo-display-config']['get']['parameters']['query'], 'brand'>, blacklistPromoDisplayIds: Array<string>): AppAction {
  return {
    type: API_CALL,
    api: FETCH_PROMO_DISPLAY_CONFIGS,
    request: () => getPromoDisplayConfig(params, blacklistPromoDisplayIds),
  }
}

// Extended query type that includes additional properties not in the contract (remove with contract update)
type ExtendedCheckPromoDisplayQuery = CheckPromoDisplayQuery & {
  sitePathBase64: string
  region: string
}

type CheckPromoDisplayConfigProps = {
  promoDisplayId: string
  offerId: string
  domainUserId?: string
  path: string
  query: ExtendedCheckPromoDisplayQuery
}

export function checkPromoDisplayConfig({ promoDisplayId, offerId, path, query }: CheckPromoDisplayConfigProps) {
  // (Remove after contract update)
  const { sitePathBase64, region, ...apiQuery } = query

  return {
    type: API_CALL,
    api: CHECK_PROMO_DISPLAY_CONFIG,
    request: () => getPromoDisplayConfigValidation(promoDisplayId, offerId, path, apiQuery).then((data) => {
      trackEvent(promotionViewEvent({
        promotionContext: { promotionName: 'promo_display', promotionType: 'prompt' },
        promotionCodeContext: {
          codeName: data.codeName ?? '',
          displayOptionKey: data.displayOptionKey ?? '',
          promoDisplayId: data.promoDisplayId,
        },
        promotionDisplayCheckContext: {
          checkPassed: data.checkPassed,
          resultCode: data.resultCode,
          promoDisplayConfigId: data.promoDisplayId,
          displayOptionKey: data.displayOptionKey ?? '',
        },
      }))
      return data
    }),
  }
}

export function resetTargetedPromo(): AppAction {
  return {
    type: RESET_TARGETED_PROMO_DETAILS,
  }
}

export function removePromoDisplay(promoDisplayId: string): AppAction {
  return {
    type: API_CALL_SUCCESS,
    api: REMOVE_PROMO_DISPLAY_CONFIG,
    data: { promoDisplayId },
  }
}

export function fetchAllVouchers(status: App.VoucherStatus): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    dispatch({
      type: API_CALL,
      api: FETCH_ALL_VOUCHERS,
      status,
      request: () => getAllVouchers({
        region: state.geo.currentRegionCode,
        status,
      }),
    })
  }
}

export function addToMetasearchBlacklist(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const { marketing } = getState()
    const updated = addPropertyToBlacklist(marketing.metasearchPropertyBlacklist, propertyId)
    dispatch({ type: SET_METASEARCH_PROPERTY_BLACKLIST, blacklist: updated })
    setCookie(METASEARCH_PROPERTY_BLACKLIST_COOKIE, serializeBlacklist(updated), hoursInSeconds(1), '/', 'Lax')
  }
}
